import React, { useState } from 'react';

const CreateQuestModal = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'side',
    difficulty: 'easy',
    experienceReward: 0,
    goldReward: 0,
    itemRewardId: '',
    requirements: '',
    isActive: true
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const questTypes = [
    { value: 'main', label: 'Principal', icon: '🌟' },
    { value: 'side', label: 'Secundária', icon: '📝' },
    { value: 'daily', label: 'Diária', icon: '📅' },
    { value: 'weekly', label: 'Semanal', icon: '📊' },
    { value: 'event', label: 'Evento', icon: '🎉' }
  ];

  const difficulties = [
    { value: 'easy', label: 'Fácil', color: 'text-green-600' },
    { value: 'medium', label: 'Médio', color: 'text-yellow-600' },
    { value: 'hard', label: 'Difícil', color: 'text-red-400' },
    { value: 'legendary', label: 'Lendário', color: 'text-purple-600' }
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Limpar erro do campo quando usuário digitar
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Descrição é obrigatória';
    }

    const expReward = parseInt(formData.experienceReward);
    const goldReward = parseInt(formData.goldReward);

    if (expReward < 0) {
      newErrors.experienceReward = 'Experiência não pode ser negativa';
    }

    if (goldReward < 0) {
      newErrors.goldReward = 'Ouro não pode ser negativo';
    }

    if (expReward === 0 && goldReward === 0 && !formData.itemRewardId) {
      newErrors.rewards = 'Pelo menos uma recompensa deve ser definida';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      const questData = {
        ...formData,
        experienceReward: parseInt(formData.experienceReward) || 0,
        goldReward: parseInt(formData.goldReward) || 0,
        itemRewardId: formData.itemRewardId || null
      };
      
      await onSubmit(questData);
      // Reset form
      setFormData({
        name: '',
        description: '',
        type: 'side',
        difficulty: 'easy',
        experienceReward: 0,
        goldReward: 0,
        itemRewardId: '',
        requirements: '',
        isActive: true
      });
      setErrors({});
      onClose();
    } catch (error) {
      console.error('Erro ao criar quest:', error);
      setErrors({ submit: 'Erro ao criar quest. Tente novamente.' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      type: 'side',
      difficulty: 'easy',
      experienceReward: 0,
      goldReward: 0,
      itemRewardId: '',
      requirements: '',
      isActive: true
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  const selectedType = questTypes.find(t => t.value === formData.type);
  const selectedDifficulty = difficulties.find(d => d.value === formData.difficulty);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black/70 backdrop-blur-sm transition-opacity"
          onClick={handleClose}
        />
        
        {/* Modal */}
        <div className="relative transform overflow-hidden rounded-2xl bg-gradient-to-br from-slate-800/95 to-slate-900/95 backdrop-blur-xl border border-white/10 px-4 pb-4 pt-5 text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
          <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <h3 className="text-xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent mb-2">
                📜 Criar Nova Quest
              </h3>
              <p className="text-sm text-gray-400">
                Adicione uma nova missão ao sistema
              </p>
            </div>

            {errors.submit && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg text-sm">
                {errors.submit}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Coluna Esquerda */}
              <div className="space-y-4">
                {/* Nome */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Nome da Quest *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Ex: A Busca pelo Tesouro Perdido"
                    className={`w-full px-4 py-2 bg-slate-700/50 border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${errors.name ? 'border-red-500/50' : 'border-white/20'}`}
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-400">{errors.name}</p>
                  )}
                </div>

                {/* Tipo */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Tipo
                  </label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    className="w-full px-4 py-2 bg-slate-700/50 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                  >
                    {questTypes.map(type => (
                      <option key={type.value} value={type.value} className="bg-slate-800 text-white">
                        {type.icon} {type.label}
                      </option>
                    ))}
                  </select>
                  {selectedType && (
                    <p className="mt-1 text-sm text-gray-400">
                      {selectedType.icon} Tipo selecionado: {selectedType.label}
                    </p>
                  )}
                </div>

                {/* Dificuldade */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Dificuldade
                  </label>
                  <select
                    name="difficulty"
                    value={formData.difficulty}
                    onChange={handleChange}
                    className="w-full px-4 py-2 bg-slate-700/50 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                  >
                    {difficulties.map(diff => (
                      <option key={diff.value} value={diff.value} className="bg-slate-800 text-white">
                        {diff.label}
                      </option>
                    ))}
                  </select>
                  {selectedDifficulty && (
                    <p className={`mt-1 text-sm ${selectedDifficulty.color.replace('600', '400')}`}>
                      Dificuldade: {selectedDifficulty.label}
                    </p>
                  )}
                </div>

                {/* Requisitos */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Requisitos (opcional)
                  </label>
                  <textarea
                    name="requirements"
                    value={formData.requirements}
                    onChange={handleChange}
                    rows={2}
                    placeholder="Ex: Nível 10, Quest anterior completa..."
                    className="w-full px-4 py-2 bg-slate-700/50 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none text-sm transition-all"
                  />
                </div>
              </div>

              {/* Coluna Direita */}
              <div className="space-y-4">
                {/* Recompensas */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-3">
                    Recompensas
                  </label>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">
                        🔥 Experiência (XP)
                      </label>
                      <input
                        type="number"
                        name="experienceReward"
                        min="0"
                        value={formData.experienceReward}
                        onChange={handleChange}
                        className={`w-full px-4 py-2 bg-slate-700/50 border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${errors.experienceReward ? 'border-red-500/50' : 'border-white/20'}`}
                      />
                      {errors.experienceReward && (
                        <p className="mt-1 text-sm text-red-400">{errors.experienceReward}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs text-gray-400 mb-1">
                        🪙 Ouro
                      </label>
                      <input
                        type="number"
                        name="goldReward"
                        min="0"
                        value={formData.goldReward}
                        onChange={handleChange}
                        className={`w-full px-4 py-2 bg-slate-700/50 border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${errors.goldReward ? 'border-red-500/50' : 'border-white/20'}`}
                      />
                      {errors.goldReward && (
                        <p className="mt-1 text-sm text-red-400">{errors.goldReward}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs text-gray-400 mb-1">
                        🎁 Item (ID do item)
                      </label>
                      <input
                        type="text"
                        name="itemRewardId"
                        value={formData.itemRewardId}
                        onChange={handleChange}
                        placeholder="ID do item de recompensa"
                        className="w-full px-4 py-2 bg-slate-700/50 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                      />
                    </div>

                    {errors.rewards && (
                      <p className="text-sm text-red-400">{errors.rewards}</p>
                    )}
                  </div>
                </div>

                {/* Status */}
                <div>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleChange}
                      className="w-4 h-4 text-purple-600 bg-slate-700 border-white/20 rounded focus:ring-purple-500 focus:ring-offset-slate-800"
                    />
                    <span className="text-sm font-medium text-gray-300">
                      ✅ Quest ativa (disponível para jogadores)
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* Descrição - Largura Total */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Descrição da Quest *
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                placeholder="Descreva detalhadamente a quest, objetivos, história e como completá-la..."
                className={`w-full px-4 py-2 bg-slate-700/50 border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none transition-all ${
                  errors.description ? 'border-red-500/50' : 'border-white/20'
                }`}
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-400">{errors.description}</p>
              )}
            </div>

            {/* Botões */}
            <div className="mt-6 flex space-x-3">
              <button
                type="button"
                onClick={handleClose}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 disabled:bg-slate-800/50 disabled:text-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors border border-white/10"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-all duration-200 shadow-lg hover:shadow-purple-500/50 font-medium"
              >
                {loading ? '⏳ Criando...' : '✨ Criar Quest'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateQuestModal;